<template>
  <main>
    <div data-cy="form-label" class="b-dialog__top-panel">
      <h2 v-if="counterparty">
        {{ $t('CONTENT.counterparty.counterparty_edit') }}
      </h2>
      <h2 v-else>
        {{ $t('CONTENT.counterparty.counterparty_creation') }}
      </h2>

      <button
        class="b-btn b-btn_border_none"
        @click.prevent="closeCounterpartyDialog"
      >
        <dynamic-icon icon="close" />
      </button>
    </div>

    <form
      class="col-12 b-form b-dialog__form"
      @submit.prevent="onSubmit"
    >
      <server-error :error="error" />

      <div class="row mt-4 b-form__row_type_flex b-form__row_type_flex-end">
        <div class="col-6">
          <div class="b-form__container">
            <label>
              {{ $t('CONTENT.counterparty.counterparty_type') }}
              <span class="b-form__required">*</span>
            </label>

            <cool-select
              v-model="selectedType"
              :list="allCounterpartyTypes"
              option-value="typeFullName"
              option-text="displayName"
              :is-disabled="cannotSwitchType"
              :placeholder="$t('CONTENT.choose')"
            />
          </div>
        </div>

        <div class="col-6">
          <div class="b-form__container b-form__container_type_flex">
            <div
              v-if="isEditCounterparty"
              class="mb-2 mr-6"
              :class="{ 'b-checkbox is-disabled': isEditCounterparty }"
            >
              <checkbox
                v-model="formData.isIntragroupTurnover"
                class="b-checkbox"
              >
                {{ $t('CONTENT.counterparty.VGO') }}
              </checkbox>
            </div>

            <div class="col-12">
              <div class="b-form__container">
                <label class="mr-5">
                  {{ isVatPayerText }}
                </label>

                <div class="b-form__container_type_flex">
                  <div class="mr-2">
                    <button
                      class="b-btn b-btn_type_secondary"
                      @click.prevent="openVatSettings"
                    >
                      {{ $t('CONTENT.counterparty.vatSettings') }}
                    </button>
                  </div>

                  <div>
                    {{ $t('CONTENT.counterparty.rateVAT') }}: {{ vatRateText }}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <CounterpartyLegalPersonForm
        v-if="formData && selectedType === 'Legal person'"
        :form-data="formData"
        @on-error="displayError"
      />

      <CounterpartyNaturalPersonAndSP
        v-if="formData && selectedType === 'Natural person'"
        :form-data="formData"
        @on-error="displayError"
      />

      <CounterpartyNaturalPersonAndSP
        v-if="formData && selectedType === 'Sole proprietorship'"
        :form-data="formData"
        @on-error="displayError"
      />

      <div v-if="formData" class="row mt-4">
        <div class="col-6 offset-6">
          <div class="b-form__container b-form__container_type_flex b-form__container_type_flex_end">
            <button
              data-cy="save-button"
              class="b-btn b-btn_type_primary"
              type="submit"
            >
              {{ formData.idInDB ? $t('CONTENT.save') : $t('CONTENT.counterparty.create') }}
            </button>

            <button
              data-cy="cancel-button"
              class="b-btn b-btn_type_empty m-2"
              type="button"
              @click.prevent="closeCounterpartyDialog"
            >
              {{ $t('CONTENT.cancel') }}
            </button>
          </div>
        </div>
      </div>
    </form>
  </main>
</template>

<script>
import { mapGetters } from 'vuex';
import _ from 'lodash';

import CounterpartyLegalPersonForm from './LegalPersonForm.vue';
import CounterpartyNaturalPersonAndSP from './NaturalPersonAndSPForm.vue';

import CounterpartyLegalPerson from '../../../../models/counterparty/CounterpartyLegalPerson';
import CounterpartyNaturalPerson from '../../../../models/counterparty/CounterpartyNaturalPerson';
import CounterpartySoleProprietorship from '../../../../models/counterparty/CounterpartySoleProprietorship';

import counterPartyValidatorSetup from '../../../../utils/validators/counterparty/setup';

export default {
  name: 'GeneralForm',

  components: {
    CounterpartyLegalPersonForm,
    CounterpartyNaturalPersonAndSP,
  },

  props: {
    counterparty: {
      type: Object,
      default: null,
    },
    counterpartyVatHistoryList: {
      type: Array,
      default: function () {
        return [];
      },
    },
  },

  data() {
    return {
      selectedType: null,
      formData: null,
      error: null,
      editActionType: null,
      createActionType: null,
      validatorSetup: counterPartyValidatorSetup,
      isEditCounterparty: false,
      isReadOnlyMode: true,
      role: null,
    };
  },

  computed: {
    ...mapGetters([
      'allCounterpartyTypes',
      'getAddedAdresses',
    ]),

    cannotSwitchType() {
      return !!this.counterparty;
    },

    lastVatRate() {
      if (
        !this.formData ||
        !Array.isArray(this.formData.counterpartyVatHistoryList) ||
        this.formData.counterpartyVatHistoryList.length === 0
      ) {
        return null;
      }

      return this.formData.counterpartyVatHistoryList[
      this.formData.counterpartyVatHistoryList.length - 1
        ];
    },

    isVatPayerText() {
      let last = this.lastVatRate;

      if (!last || last.isSubjectToVAT !== true) {
        return this.$t('CONTENT.counterparty.notHasVAT');
      }

      return this.$t('CONTENT.counterparty.hasVAT');
    },

    vatRateText() {
      let last = this.lastVatRate;

      if (!last || last.rateVAT === null || last.rateVAT === undefined) {
        return '0%';
      }

      return last.rateVAT + '%';
    },
  },

  watch: {
    selectedType() {
      this.chooseType();

      if (!this.counterparty) {
        this.rebuildFormData();
      }

      if (this.formData) {
        this.formData.counterpartyType = this.selectedType;
      }
    },

    counterpartyVatHistoryList(val) {
      if (this.formData) {
        this.formData.counterpartyVatHistoryList = val || [];
      }
    },

    allCounterpartyTypes: {
      handler() {
        if (!this.allCounterpartyTypes || !Array.isArray(this.allCounterpartyTypes)) {
          return;
        }

        if (!this.selectedType && this.formData) {
          this.setSelectedTypeFromFormData();
        } else {
          let match = this.findMatchingOption(this.selectedType);
          if (!match && this.formData) {
            this.setSelectedTypeFromFormData();
          }
        }
      },
      immediate: true,
    },

    counterparty: {
      immediate: false,
      handler: function (newVal) {
        if (newVal) {
          this.isEditCounterparty = !!newVal.isIntragroupTurnover;
          this.formData = _.cloneDeep(newVal);

          if (Array.isArray(newVal.counterpartyVatHistoryList) && newVal.counterpartyVatHistoryList.length > 0) {
            this.formData.counterpartyVatHistoryList = _.cloneDeep(newVal.counterpartyVatHistoryList);
          } else {
            this.formData.counterpartyVatHistoryList = this.counterpartyVatHistoryList || [];
          }

          this.setSelectedTypeFromFormData();
          this.chooseType();
        } else {
          this.isEditCounterparty = false;
          this.selectedType = 'Legal person';
          this.formData = CounterpartyLegalPerson.createNew();
          this.formData.counterpartyType = this.selectedType;
          this.formData.counterpartyVatHistoryList = [];
          this.chooseType();
        }
      }
    }
  },

  created: function () {
    this.role = localStorage.getItem('role');
    this.isReadOnlyMode = this.role !== 'Admin';

    if (this.counterparty) {
      this.isEditCounterparty = !!this.counterparty.isIntragroupTurnover;
      this.formData = _.cloneDeep(this.counterparty);

      if (Array.isArray(this.counterparty.counterpartyVatHistoryList) && this.counterparty.counterpartyVatHistoryList.length > 0) {
        this.formData.counterpartyVatHistoryList = _.cloneDeep(this.counterparty.counterpartyVatHistoryList);
      } else {
        this.formData.counterpartyVatHistoryList = this.counterpartyVatHistoryList || [];
      }

      this.setSelectedTypeFromFormData();
    } else {
      this.selectedType = 'Legal person';
      this.formData = CounterpartyLegalPerson.createNew();
      this.formData.counterpartyType = this.selectedType;
      this.formData.counterpartyVatHistoryList = [];
    }

    this.chooseType();
  },

  methods: {
    findMatchingOption(value) {
      if (!value || !Array.isArray(this.allCounterpartyTypes)) {
        return null;
      }

      for (let i = 0; i < this.allCounterpartyTypes.length; i++) {
        let opt = this.allCounterpartyTypes[i];

        if (opt && opt.typeFullName && opt.typeFullName === value) {
          return opt;
        }

        if (opt && opt.displayName && opt.displayName === value) {
          return opt;
        }

        if (opt && (opt.type === value || opt.code === value || opt.name === value)) {
          return opt;
        }
      }

      return null;
    },

    setSelectedTypeFromFormData() {
      if (!this.formData) {
        return;
      }

      let candidate = this.formData.counterpartyType || this.formData.type || this.formData.typeFullName || this.formData.displayName || null;

      if (!candidate) {
        return;
      }

      let match = this.findMatchingOption(candidate);

      if (match && match.typeFullName) {
        this.selectedType = match.typeFullName;
      } else {
        this.selectedType = candidate;
      }

      if (this.formData) {
        this.formData.counterpartyType = this.selectedType;
      }
    },

    chooseType() {
      if (this.selectedType === 'Legal person') {
        this.editActionType = 'updateCounterpartyLegalPerson';
        this.createActionType = 'createCounterpartyLegalPerson';
      } else if (this.selectedType === 'Natural person') {
        this.editActionType = 'updateCounterpartyNaturalPerson';
        this.createActionType = 'createCounterpartyNaturalPerson';
      } else if (this.selectedType === 'Sole proprietorship') {
        this.editActionType = 'updateCounterpartySoleProprietorship';
        this.createActionType = 'createCounterpartySoleProprietorship';
      } else {
        this.editActionType = null;
        this.createActionType = null;
      }
    },

    rebuildFormData() {
      if (this.counterparty) {
        return;
      }

      let created = null;

      if (this.selectedType === 'Legal person') {
        created = CounterpartyLegalPerson.createNew();
      } else if (this.selectedType === 'Natural person') {
        created = CounterpartyNaturalPerson.createNew();
      } else if (this.selectedType === 'Sole proprietorship') {
        created = CounterpartySoleProprietorship.createNew();
      }

      if (created) {
        this.formData = created;
        this.formData.counterpartyType = this.selectedType;
        this.formData.counterpartyVatHistoryList = [];
      } else {
        if (!this.formData) {
          this.formData = CounterpartyLegalPerson.createNew();
          this.formData.counterpartyType = 'Legal person';
          this.formData.counterpartyVatHistoryList = this.counterpartyVatHistoryList || [];
          this.selectedType = 'Legal person';
          this.chooseType();
        }
      }
    },

    openVatSettings() {
      this.$emit('openVatSettings', {
        rates: (this.formData && this.formData.counterpartyVatHistoryList) || []
      });
    },

    async onSubmit() {
      try {
        this.error = null;
        this.formData.addresses = this.getAddedAdresses;
        let action = this.formData.idInDB ? this.editActionType : this.createActionType;

        if (typeof action !== 'string') {
          throw new Error('Vuex action type is invalid');
        }

        await this.$store.dispatch(action, _.cloneDeep(this.formData));
        this.closeCounterpartyDialog();
      } catch (e) {
        this.error = e;
      }
    },

    closeCounterpartyDialog() {
      this.$emit('close');
    },

    displayError(e) {
      this.error = e;
    },
  },
};
</script>
